import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import useStyles from '../styles';
import CardHeader from '../../../../components/General/CardHeader';
import {
  fetchWeatherForecast,
  fetchWeatherHistoricalData,
  fetchWeatherForecastWithPastDays,
  Forecast,
  getWeatherEmoji,
} from '../utils';
import DailyForecast from './DailyForecast';
import { ReduxState } from '../../../../types';

interface WeatherForecastProps {
  hideTitle?: boolean;
  hideUpdateDate?: boolean;
  hideInfoSource?: boolean;
  specificDates?: string[];
  showHistoricalData?: boolean;
  compact?: boolean;
}

const HISTORICAL_DATA_DELAY_DAYS = 4;

const getAdjustedToday = (): Date => {
  const adjustedToday = new Date();
  adjustedToday.setDate(adjustedToday.getDate() - HISTORICAL_DATA_DELAY_DAYS);
  adjustedToday.setHours(0, 0, 0, 0);
  return adjustedToday;
};

const categorizeDatesByDataSource = (specificDates: string[]) => {
  const adjustedToday = getAdjustedToday();
  const pastDates: string[] = [];
  const futureDates: string[] = [];

  specificDates.forEach((date) => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    if (targetDate < adjustedToday) {
      pastDates.push(date);
    } else {
      futureDates.push(date);
    }
  });

  return { pastDates, futureDates };
};

const getDayData = (
  date: string,
  forecastData: Forecast | null,
  historicalData: { [key: string]: any } | null,
) => {
  const adjustedToday = getAdjustedToday();
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  const isPastDate = targetDate < adjustedToday;

  if (isPastDate && historicalData && historicalData[date]) {
    return historicalData[date];
  }

  if (forecastData && forecastData[date as unknown as keyof Forecast]) {
    return forecastData[date as unknown as keyof Forecast];
  }

  return {
    day: 'N/A',
    date: `${String(targetDate.getDate() + 1).padStart(2, '0')}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`,
    minTemperature: null,
    maxTemperature: null,
    RainMm: null,
    RainProbability: null,
    eto: null,
    weather: 'N/A',
  };
};

const useWeatherData = (
  latitude: number,
  longitude: number,
  specificDates?: string[],
) => {
  const [forecastData, setForecastData] = useState<Forecast | null>(null);
  const [historicalData, setHistoricalData] = useState<{ [key: string]: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchWeatherData = async () => {
    if (!latitude || !longitude) return;

    try {
      setLoading(true);

      if (specificDates?.length) {
        const weatherInformation = await fetchWeatherForecastWithPastDays(
          latitude,
          longitude,
          HISTORICAL_DATA_DELAY_DAYS,
        );
        setForecastData(weatherInformation);

        const { pastDates } = categorizeDatesByDataSource(specificDates);

        if (pastDates.length > 0) {
          const historicalInformation = await fetchWeatherHistoricalData(
            latitude,
            longitude,
            pastDates[0],
            pastDates[pastDates.length - 1],
          );
          setHistoricalData(historicalInformation);
        } else {
          setHistoricalData(null);
        }
      } else {
        const weatherInformation = await fetchWeatherForecast(latitude, longitude);
        setForecastData(weatherInformation);
        setHistoricalData(null);
      }

      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      enqueueSnackbar('Error al actualizar el clima', { variant: 'error' });
      setLastUpdated('Error - Intenta nuevamente');
    } finally {
      setLoading(false);
    }
  };

  return {
    forecastData,
    historicalData,
    loading,
    lastUpdated,
    fetchWeatherData,
  };
};

const WeatherForecast = ({
  hideTitle = false,
  hideUpdateDate = false,
  hideInfoSource = false,
  specificDates,
  compact = false,
}: WeatherForecastProps) => {
  const classes = useStyles();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);

  const localLatitude = Number(selectedFarm?.center?.lat);
  const localLongitude = Number(selectedFarm?.center?.lng);

  const {
    forecastData,
    historicalData,
    loading,
    lastUpdated,
    fetchWeatherData,
  } = useWeatherData(localLatitude, localLongitude, specificDates);

  useEffect(() => {
    fetchWeatherData();
  }, [specificDates]);

  const getContainerStyles = () => {
    if (compact) {
      return {
        flexWrap: 'nowrap' as const,
        overflowX: 'auto' as const,
        gap: '0px',
        justifyContent: 'flex-start' as const,
        '& > *': {
          flex: '0 0 auto',
          minWidth: '42px',
          maxWidth: '48px',
        },
      };
    }
    return {
      justifyContent: 'space-between' as const,
      flexWrap: 'wrap' as const,
    };
  };

  const renderDaysList = (daysData: any[]) => (
    <Box display="flex" sx={getContainerStyles()}>
      {daysData.map((dayData, index) => (
        <DailyForecast key={index} {...dayData} compact={compact} />
      ))}
    </Box>
  );

  const renderCompactTable = (daysData: any[]) => (
    <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
      <Table
        size="small"
        sx={{
          width: '100%',
          minWidth: '100%',
          tableLayout: 'fixed',
          '& td, & th': {
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            padding: '4px 2px',
            fontSize: '0.75rem',
            textAlign: 'center',
          },
          '& td:first-of-type, & th:first-of-type': {
            width: '90px',
            minWidth: '90px',
            maxWidth: '90px',
          },
          '& td:not(:first-of-type), & th:not(:first-of-type)': {
            width: `calc((100% - 90px) / ${specificDates?.length || daysData.length})`,
            minWidth: '80px',
          },
          '& .MuiTableBody-root td': {
            borderBottom: 'none',
          },
          '& .MuiTableHead-root th': {
            borderBottom: '2px solid rgba(224, 224, 224, 1)',
            padding: '8px 2px',
            fontSize: '0.75rem',
            height: '60px',
            verticalAlign: 'middle',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ borderRight: '2px solid rgba(224, 224, 224, 1)' }}>
              Día
            </TableCell>
            {daysData.map((dayData, index) => (
              <TableCell
                key={index}
                align="center"
                sx={{
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  whiteSpace: 'pre-line',
                  lineHeight: 1.2,
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    {dayData.day}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    {dayData.date}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Primera fila: Clima y lluvia */}
          <TableRow>
            <TableCell
              sx={{
                borderRight: '2px solid rgba(224, 224, 224, 1)',
                borderBottom: 'none',
                padding: '8px 4px',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Clima
              </Typography>
            </TableCell>
            {daysData.map((dayData, index) => (
              <TableCell
                key={index}
                align="center"
                sx={{
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  borderBottom: 'none',
                  padding: '8px 4px',
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1}
                  sx={{
                    minHeight: '40px',
                    justifyContent: 'center',
                  }}
                >
                  {/* Icono del clima */}
                  <Tooltip title={dayData.weather}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        fontSize: '20px',
                        minHeight: '20px',
                      }}
                    >
                      {getWeatherEmoji(dayData.weather)}
                    </Box>
                  </Tooltip>

                  {/* Lluvia si es lluvioso o chubascos */}
                  {(dayData.weather === 'Lluvioso' || dayData.weather === 'Chubascos') && dayData.RainMm && (
                    <Typography variant="caption" sx={{ fontSize: '0.45rem', color: 'blue' }}>
                      {`${dayData.RainMm} mm`}
                    </Typography>
                  )}

                  {/* Probabilidad de lluvia si es lluvioso o chubascos */}
                  {(dayData.weather === 'Lluvioso' || dayData.weather === 'Chubascos') && dayData.RainProbability && (
                    <Typography variant="caption" sx={{ fontSize: '0.45rem', color: 'blue' }}>
                      {`${dayData.RainProbability}%`}
                    </Typography>
                  )}
                </Box>
              </TableCell>
            ))}
          </TableRow>

          {/* Segunda fila: Temperatura */}
          <TableRow>
            <TableCell
              sx={{
                borderRight: '2px solid rgba(224, 224, 224, 1)',
                borderBottom: 'none',
                padding: '8px 4px',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                Temperatura
              </Typography>
            </TableCell>
            {daysData.map((dayData, index) => (
              <TableCell
                key={index}
                align="center"
                sx={{
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  borderBottom: 'none',
                  padding: '8px 4px',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    minHeight: '40px',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {dayData.minTemperature !== null && dayData.maxTemperature !== null
                      ? `${Math.round(dayData.minTemperature)}°. ${Math.round(dayData.maxTemperature)}°.`
                      : 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>

          {/* Tercera fila: ETO */}
          <TableRow>
            <TableCell
              sx={{
                borderRight: '2px solid rgba(224, 224, 224, 1)',
                borderBottom: 'none',
                padding: '8px 4px',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                ET0
              </Typography>
            </TableCell>
            {daysData.map((dayData, index) => (
              <TableCell
                key={index}
                align="center"
                sx={{
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  borderBottom: 'none',
                  padding: '8px 4px',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    minHeight: '40px',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                    {dayData.eto ?? 'N/A'}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    if (specificDates?.length) {
      const daysToShow = specificDates.map((date) => getDayData(
        date, forecastData, historicalData,
      ));
      return compact ? renderCompactTable(daysToShow) : renderDaysList(daysToShow);
    }

    if (forecastData) {
      const daysToShow = Object.values(forecastData);
      return compact ? renderCompactTable(daysToShow) : renderDaysList(daysToShow);
    }

    return <Typography>No hay datos disponibles</Typography>;
  };

  const renderHeader = () => {
    if (hideTitle) return null;

    return (
      <CardHeader
        title="Clima"
        ActionButton={
          !hideUpdateDate ? (
            <Box display="flex" alignItems="center">
              <Box display="flex" flexDirection="column" alignItems="flex-end" marginRight={1}>
                <Typography variant="body2" className={classes.lastUpdatedText}>
                  Última actualización:
                </Typography>
                <Typography variant="body2" className={classes.lastUpdatedDate}>
                  {lastUpdated || 'N/A'}
                </Typography>
              </Box>
              <IconButton onClick={fetchWeatherData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Box>
          ) : undefined
        }
        typographyVariant="h3"
        additionalLink={hideInfoSource ? undefined : 'https://open-meteo.com/en/docs'}
      />
    );
  };

  if (!selectedFarm) {
    return (
      <Card>
        <CardContent>
          {!hideTitle && (
            <Typography variant="h3" color="primary" gutterBottom display="inline">
              Clima
            </Typography>
          )}
          {!hideTitle && <Divider className={classes.divider} />}
          <Typography className={classes.noFarmSelectedText}>
            Elige un campo para ver el clima
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Si está en modo compact, no usar Card para mejor alineación
  if (compact) {
    return (
      <>
        {renderHeader()}
        {!hideTitle && <Divider className={classes.divider} />}
        {renderContent()}
      </>
    );
  }

  return (
    <Card>
      <CardContent>
        {renderHeader()}
        {!hideTitle && <Divider className={classes.divider} />}
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default WeatherForecast;
